import { createSelector } from 'reselect';
import { RootState } from 'store/types';
import findIndex from 'lodash/findIndex';
import orderBy from 'lodash/orderBy';
import dayjs from 'dayjs';
import { GlobalSearchItem } from 'store/global/types';
import {
  ClinicNotesFilter,
  ClinicNotesListItem,
  ClinicNotesDetailState,
  ClinicNotesDownloadState,
  ClinicNotesListState,
  ClinicNotesFilterType,
  dateTimeMappers,
  sortByMapper,
  sortByDirectionMapper
} from './types';

export const clinicNotesDetailSelector = (state: RootState): ClinicNotesDetailState =>
  state.clinicNotes.detail;
export const clinicNotesDownloadSelector = (state: RootState): ClinicNotesDownloadState =>
  state.clinicNotes.download;
export const clinicNotesListSelector = (state: RootState): ClinicNotesListState =>
  state.clinicNotes.list;
export const clinicNoteAuthLiteSelector = (state: RootState): ClinicNotesDetailState =>
  state.clinicNotes.authLiteDocument;

// Auth Lite Document Selectors
export const clinicNotesAuthLiteDataSelector = createSelector(
  clinicNoteAuthLiteSelector,
  detail => detail.data
);
export const clinicNotesAuthLiteFetchingSelector = createSelector(
  clinicNoteAuthLiteSelector,
  detail => detail.isFetching
);
export const clinicNotesAuthLiteErrorSelector = createSelector(
  clinicNoteAuthLiteSelector,
  detail => detail.error
);

// Clinic Notes Detail Selectors
export const clinicNotesDetailDataSelector = createSelector(
  clinicNotesDetailSelector,
  detail => detail.data
);
export const clinicNotesDetailFetchingSelector = createSelector(
  clinicNotesDetailSelector,
  detail => detail.isFetching
);
export const clinicNotesDetailErrorSelector = createSelector(
  clinicNotesDetailSelector,
  detail => detail.error
);

// Clinic Notes List Selectors
export const clinicNotesListDataSelector = createSelector(clinicNotesListSelector, list => {
  // Get list data to be sorted after applying all filters
  const getFilteredListData = () => {
    // If filters are applied, filter clinic notes
    if (list.filters?.length > 0) {
      const format = 'YYYY-MM-DD HH:mm:ss';
      return list.data?.filter((item: ClinicNotesListItem) => {
        // Get index of date filter
        const indexOfDateFilter = findIndex(
          list.filters,
          filter =>
            filter.filterType === ClinicNotesFilterType.dateTime ||
            filter.filterType === ClinicNotesFilterType.dateRange
        );
        // Assign mutable filters
        const [...filters] = list.filters;
        // Extract single date type filter if there is one
        const dateFilter =
          indexOfDateFilter !== -1 ? filters.splice(indexOfDateFilter, 1)?.[0] : null;
        // Get the indexDate to evaluate against date filter
        const evalTime = dayjs(item.indexDate).format(format);
        // has filters other than a date filter
        const hasOtherFilterRequirements = filters?.length > 0;
        // meets any of the non-date filter requirements
        const meetsOtherFilterRequirements = filters.some((filter: ClinicNotesFilter) => {
          if (filter.filterType === ClinicNotesFilterType.noteCategory) {
            return item.noteCategory === filter.value;
          }
          // TODO: enable for when providers are added back to clinic notes
          // if (filter.filterType === ClinicNotesFilterType.provider) {
          //   return item.provider.name === filter.value;
          // }
          return false;
        });

        // If a date filter is applied and item has an indexDate, check date time/range
        if (dateFilter && item.indexDate) {
          if (dateFilter.filterType === ClinicNotesFilterType.dateTime) {
            const [num, unit] = dateTimeMappers[dateFilter.value];
            const currentTime = dayjs()
              .endOf('day')
              .format(format);
            const timeInPast = dayjs()
              .startOf('day')
              .subtract(num, unit)
              .format(format);
            const isInDateTimeRange = evalTime <= currentTime && evalTime >= timeInPast;
            // If we have other filter requirements to keep in mind, check both date time
            // and if item meets those other requirements
            if (hasOtherFilterRequirements) {
              return isInDateTimeRange && meetsOtherFilterRequirements;
            }
            // Else, just check by date time
            return isInDateTimeRange;
          }
          // Same prinicles apply but for dateRange type
          if (dateFilter.filterType === ClinicNotesFilterType.dateRange) {
            // FROM date in the past TO date in the preset/future
            const fromDate = dayjs(dateFilter?.value?.from)
              .startOf('day')
              .format(format);
            const toDate = dayjs(dateFilter?.value?.to)
              .endOf('day')
              .format(format);
            const isInDateRange = evalTime >= fromDate && evalTime <= toDate;
            if (hasOtherFilterRequirements) {
              return isInDateRange && meetsOtherFilterRequirements;
            }
            return isInDateRange;
          }
        }
        // Else, just check by non-date requirements
        return meetsOtherFilterRequirements;
      });
    }
    return list.data;
  };
  // return sorted list
  return orderBy(
    getFilteredListData(),
    [sortByMapper[list.sortBy]],
    [sortByDirectionMapper[list.sortBy]]
  );
});

export const clinicNotesListFetchingSelector = createSelector(
  clinicNotesListSelector,
  list => list.isFetching
);

export const clinicNotesListErrorSelector = createSelector(
  clinicNotesListSelector,
  list => list.error
);

export const clinicNotesListRequestStateSelector = createSelector(
  clinicNotesListSelector,
  list => list.requestState
);

export const clinicNotesListFilters = createSelector(clinicNotesListSelector, list => list.filters);

export const clinicNotesListFilterOptions = createSelector(
  clinicNotesListSelector,
  list => list.filterOptions
);

export const clinicNotesListSort = createSelector(clinicNotesListSelector, list => list.sortBy);

export const shareClinicNotesSelector = (state: RootState) =>
  state.clinicNotes.shareClinicNotes.isSending;

export const searchFormattedClinicNotes = createSelector(clinicNotesListDataSelector, data => {
  return data.map(
    (item: ClinicNotesListItem): GlobalSearchItem => {
      return {
        title: item?.noteType,
        date: item?.createDate,
        keywords: [item?.noteCategory, item?.noteType],
        navWebPath: `/u/health-record/clinic-notes-and-documents/${item?.description}`,
        navParams: { clinicNote: item },
        icon: 'ClinicNotes',
        category: 'ClinicNotes'
      };
    }
  );
});
